import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { catchError, map, Observable, of } from 'rxjs'
import { Backend } from 'src/app/shared/enums/enums'
import { Word } from 'src/app/shared/models/main/word'
import { DdcLoggerService } from '../log/ddc-logger.service'

@Injectable({
  providedIn: 'root',
})
export class WordService {
  constructor(
    private http: HttpClient,
    private logger: DdcLoggerService,
  ) {}

  get_words(): any {
    this.logger.info('WORD | get_words')

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    return this.http.get(`${Backend.HOST_CONTAINER + Backend.WORD}`, httpOptions).pipe(
      map((res: any) => {
        return res.results
      }),
      catchError((error: any) => {
        const errorMessage = error.error
        this.logger.error('WORD | get_words | catchError | errorMessage: ' + errorMessage)
        return of(errorMessage)
      }),
    )
  }

  get_searched_words(target: string, maxRows: number = 10): any {
    this.logger.info(
      'WORD/Search | get_searched_words | target: ' + target + ' | maxRows: ' + maxRows,
    )

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    // limit max rows to 10
    if (maxRows > 10 || maxRows < 1) maxRows = 10

    return this.http
      .get(
        `${Backend.HOST_CONTAINER + Backend.WORD}search/?max=${maxRows}&word=${target}`,
        httpOptions,
      )
      .pipe(
        map((res: any) => {
          return res
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error(
            'WORD/Search | get_searched_words | catchError | errorMessage: ' + errorMessage,
          )
          return of(errorMessage)
        }),
      )
  }

  get_words_page(page: number, pageSize: number): Observable<{ results: Word[]; count: number }> {
    this.logger.info('WORD | get_words_page | page: ' + page + ' | pageSize: ' + pageSize)

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    return this.http
      .get<{
        results: Word[]
        count: number
      }>(`${Backend.HOST_CONTAINER + Backend.WORD}?page=${page}&pageSize=${pageSize}`, httpOptions)
      .pipe(
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('WORD | get_words_page | catchError | errorMessage: ' + errorMessage)
          throw error // Re-throw to allow proper error handling
        }),
      )
  }

  get_user_words_page(username: string, page: number, pageSize: number): any {
    this.logger.info(
      'WORD/username | get_user_words_page | username: ' +
        username +
        ' | page: ' +
        page +
        ' | pageSize: ' +
        pageSize,
    )

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    return this.http
      .get(
        `${Backend.HOST_CONTAINER + Backend.WORD}?username=${username}&page=${page}&pageSize=${pageSize}`,
        httpOptions,
      )
      .pipe(
        map((res: any) => {
          return res
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error(
            'WORD/username | get_user_words_page | catchError | errorMessage: ' + errorMessage,
          )
          return of(errorMessage)
        }),
      )
  }

  get_dic_words_page(dic_id: number, page: number, pageSize: number): any {
    this.logger.info(
      'DIC/WORD | get_dic_words_page | dic_id: ' +
        dic_id +
        ' | page: ' +
        page +
        ' | pageSize: ' +
        pageSize,
    )

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    return this.http
      .get(
        `${Backend.HOST_CONTAINER + Backend.DIC}${dic_id}/words/?page=${page}&pageSize=${pageSize}`,
        httpOptions,
      )
      .pipe(
        map((res: any) => {
          return res
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error(
            'DIC/WORD | get_dic_words_page | catchError | errorMessage: ',
            errorMessage,
          )
          return of(errorMessage)
        }),
      )
  }

  add_word(word: any): any {
    this.logger.info('WORD | add_word | word: ' + word)

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    const userData = {
      language: word.language,
      article: word.article,
      word: word.word,
      pos: word.pos,
      example: word.example,
      definition: word.definition,
      category: word.category,
      tags: word.tags,
      dictionaries: word.dictionaries,
      privacy: word.privacy,
    }

    return this.http.post(`${Backend.HOST_CONTAINER + Backend.WORD}`, userData, httpOptions).pipe(
      map((res: any) => {
        return { success: true, res: res }
      }),
      catchError((error: any) => {
        const errorMessage = error.error
        this.logger.error('WORD | add_word | catchError | errorMessage: ', errorMessage)
        return of({ success: false, res: errorMessage })
      }),
    )
  }

  edit_word(word_data: any): any {
    this.logger.info('WORD | edit_word | word_data: ' + word_data)

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    return this.http
      .patch(
        `${Backend.HOST_CONTAINER + Backend.WORD + word_data.id + '/'}`,
        word_data,
        httpOptions,
      )
      .pipe(
        map((res: any) => {
          return { success: true, res: res }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('WORD | edit_word | catchError | errorMessage: ' + errorMessage)
          return of({ success: false, res: errorMessage })
        }),
      )
  }

  get_word(word_id: any): any {
    this.logger.info('WORD | get_word | word_id: ' + word_id)

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    return this.http
      .get(`${Backend.HOST_CONTAINER + Backend.WORD + word_id + '/'}`, httpOptions)
      .pipe(
        map((res: any) => {
          return { success: true, res: res }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('WORD | get_word | catchError | errorMessage: ' + errorMessage)
          return of({ success: false, res: errorMessage })
        }),
      )
  }

  delete_word(word_id: number): any {
    this.logger.info('WORD | delete_word | word_id: ' + word_id)

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    return this.http
      .delete(`${Backend.HOST_CONTAINER + Backend.WORD + word_id + '/'}`, httpOptions)
      .pipe(
        map((res: any) => {
          return { success: true, res: res }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('WORD | delete_word | catchError | errorMessage: ' + errorMessage)
          return of({ success: false, res: errorMessage })
        }),
      )
  }
}
